import { useEffect, type ReactNode } from 'react';
import { useMobile } from '../../hooks/useMobile';
import { X } from '../../lib/icons';

interface Props {
  title?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Desktop max-width for the centered-card variant. Default 440 (menus/dialogs, not document viewers). */
  desktopWidth?: number;
}

// The one shared overlay component for the redesign. No Modal/Sheet/Dropdown
// component existed anywhere in the codebase before this — three separate
// files (TopBar.tsx's Modal, TasksPanel.tsx's AddTaskModal, ChatHeader.tsx's
// EditContactDialog) each hand-rolled the same fixed-overlay-with-backdrop
// pattern. This adds what none of them had: Escape-to-close and dialog a11y.
export default function Sheet({ title, onClose, children, footer, desktopWidth = 440 }: Props) {
  const isMobile = useMobile();

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(0,0,0,.5)',
        animation: 'rt-scrim-in .15s ease-out',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={
          isMobile
            ? {
                width: '100%', maxHeight: '85vh',
                background: 'var(--vs1)', borderRadius: '14px 14px 0 0',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
                animation: 'rt-sheet-up .2s ease-out',
                paddingBottom: 'env(safe-area-inset-bottom)',
              }
            : {
                width: '90vw', maxWidth: desktopWidth, maxHeight: '85vh',
                background: 'var(--vs1)', border: '1px solid var(--shell-border)', borderRadius: 8,
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
                boxShadow: '0 24px 64px rgba(0,0,0,.5)',
              }
        }
      >
        {isMobile && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 2px' }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--vb2)' }} />
          </div>
        )}

        {title && (
          <div style={{
            padding: isMobile ? '4px 18px 12px' : '14px 20px',
            borderBottom: '1px solid var(--shell-border)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
          }}>
            <span style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--shell-text)' }}>{title}</span>
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                background: 'none', border: 'none', color: 'var(--shell-xs)',
                cursor: 'pointer', lineHeight: 1, padding: '2px 6px', borderRadius: 3,
                display: 'flex', alignItems: 'center',
              }}
            >
              <X size={16} strokeWidth={1.8} />
            </button>
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {children}
        </div>

        {footer && (
          <div style={{ padding: '12px 18px', borderTop: '1px solid var(--shell-border)', flexShrink: 0 }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
